import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import EmailMessagePage from "./EmailMessagePage";
import MailComposer from "../components/mail/MailComposer";
import ChatGPTMailConnection from "../components/mail/ChatGPTMailConnection";
import axios from "axios";
import { ArrowLeft, Mail, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API_ORIGIN = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const MAIL_API = `${API_ORIGIN}/api/mail-connections/icloud`;
// Separate client: mail and passwords must not enter offline caches or diagnostics.
const mailClient = axios.create({ withCredentials: true, timeout: 60000 });
const button = "rounded-lg border px-4 py-3 text-base disabled:opacity-50";

function errorMessage(error, fallback, operation) {
  const code = error?.response?.status;
  if (code === 401) return "Your NDG session has expired. Sign in again.";
  if (code === 403) return "An instructor account is needed to connect iCloud Mail.";
  if (code === 400) return "Check the iCloud address and Apple app-specific password.";
  if (code === 409) return operation === "connect"
    ? "Apple rejected the email address or app-specific password. Check both and try connecting again. Use the password generated by Apple for NDG."
    : "Search again. If iCloud sign-in was rejected, use Update connection below.";
  if (code === 404) return "The connection or message was not found. Refresh the connection or search again.";
  if (code === 422) return "Check your email, app-specific password or search term. Try DVSA or Part 3.";
  if (code === 429) return "Mail is busy. Please try again shortly.";
  if (code === 503) return "iCloud secure storage needs checking, or the connection needs updating.";
  if (code === 502) return "NDG could not reach or read iCloud Mail. Please try again.";
  if (error?.isAxiosError && !error.response) return "The NDG server did not respond. Please try again.";
  return fallback;
}

function checkedStatus(response) {
  if (typeof response?.data?.connected !== "boolean" || response.data.provider !== "icloud"
      || response.data.access !== "read_only") throw new Error("Unexpected connection response");
  return response.data;
}

export default function EmailConnectionsSettings() {
  const location = useLocation();
  const view = new URLSearchParams(location.search).get("view");
  if (view === "chatgpt") return <ChatGPTMailConnection />;
  const isMessagePage = view === "message";
  return isMessagePage ? <EmailMessagePage /> : <ConnectionSettingsContent />;
}

function ConnectionSettingsContent() {
  const { user } = useAuth();
  const [status, setStatus] = useState({ connected: false });
  const [email, setEmail] = useState(user?.email || "");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [statusReady, setStatusReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const [editing, setEditing] = useState(false);
  const [query, setQuery] = useState("Part 3");
  const [senderScope, setSenderScope] = useState("all");
  const [searchPage, setSearchPage] = useState(null);
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setLoading(true); setStatusReady(false); setResults([]); setSelected(null); setPassword("");
    mailClient.get(`${MAIL_API}/status`, { signal: controller.signal })
      .then((response) => {
        const next = checkedStatus(response);
        if (active) {
          setStatus(next); setStatusReady(true); setEmail(next.email || user?.email || "");
          setMessage(next.configured === false ? "iCloud secure storage needs setting up on the server." : "");
        }
      })
      .catch((error) => { if (active) setMessage(errorMessage(error, "Could not check the iCloud connection.")); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [reload, user?.id, user?.email]);

  const connect = async (event) => {
    event.preventDefault();
    if (busy || loading || !statusReady) return;
    setBusy(true); setMessage("Connecting and saving securely...");
    try {
      const next = checkedStatus(await mailClient.post(`${MAIL_API}/connect`, {
        email: email.trim(), app_specific_password: password,
      }));
      if (!next.connected) throw new Error("Connection not confirmed");
      setStatus(next); setEditing(false); setResults([]); setSelected(null);
      setMessage("iCloud connection saved. You can now search all your email folders below.");
    } catch (error) {
      setMessage(errorMessage(error, "Connection was not confirmed. Please try again.", "connect"));
    } finally { setPassword(""); setBusy(false); }
  };

  const disconnect = async () => {
    if (busy) return;
    setBusy(true); setMessage("Disconnecting...");
    try {
      const next = checkedStatus(await mailClient.delete(MAIL_API));
      if (next.connected) throw new Error("Disconnect not confirmed");
      setStatus(next); setResults([]); setSearchPage(null); setSelected(null); setEditing(false); setPassword("");
      setMessage("Disconnected. The saved password has been removed from NDG.");
    } catch (error) { setMessage(errorMessage(error, "Disconnect was not confirmed.")); }
    finally { setBusy(false); }
  };

  const runSearch = async (nextQuery = query, nextScope = senderScope, page = null) => {
    if (busy || !nextQuery.trim()) return;
    setBusy(true); setSelected(null); if (!page) setResults([]); setSearchPage(null);
    setMessage("Searching your email folders, including older messages...");
    try {
      const { data } = await mailClient.post(`${MAIL_API}/search`, {
        query: nextQuery.trim(), limit: 20, sender_scope: nextScope, cursor: page,
      });
      if (!Array.isArray(data?.results) || data.access !== "read_only"
          || data.sender_scope !== nextScope || data.mailbox !== "ALL") throw new Error("Search filter was not confirmed");
      setResults((previous) => page ? [...previous, ...data.results] : data.results);
      setSearchPage(data.next_cursor ? { query: nextQuery.trim(), scope: nextScope, cursor: data.next_cursor } : null);
      const checked = data.coverage?.relevance === "readable_content";
      setMessage(`${data.count} result(s) on this page. Results are grouped by folder. `
        + (checked ? "Part 2/3 matches are checked against the readable subject and message. " : "")
        + (data.next_cursor ? "More messages remain to check. Use More matching emails to continue. " : "End of search results. ")
        + (data.coverage?.unchecked_messages ? `${data.coverage.unchecked_messages} large message(s) could not be checked completely and are labelled as possible matches. ` : "")
        + (!data.coverage?.complete ? `Search incomplete: could not search ${(data.coverage?.failed_folders || []).join(", ") || "every folder"}.`
          : "All selectable folders searched, with no date cutoff."));
    } catch (error) { setMessage(errorMessage(error, "Search was not completed or the server has not confirmed the selected filter.")); }
    finally { setBusy(false); }
  };

  const search = (event) => {
    event.preventDefault();
    runSearch();
  };

  const searchDVSA = (term) => {
    setQuery(term); setSenderScope("dvsa");
    runSearch(term, "dvsa");
  };

  const openMessage = async (id) => {
    if (busy) return;
    setBusy(true); setSelected(null); setMessage("Opening message read-only...");
    try {
      const { data } = await mailClient.get(`${MAIL_API}/messages/${encodeURIComponent(id)}`);
      if (typeof data?.text !== "string" || data.access !== "read_only") throw new Error("Invalid message response");
      setSelected(data); setMessage("");
    } catch (error) { setMessage(errorMessage(error, "Could not open this message.")); }
    finally { setBusy(false); }
  };

  return <div className="min-h-dvh bg-slate-50 dark:bg-[#0c1120] text-slate-900 dark:text-slate-100">
    <main className="max-w-2xl mx-auto px-4 py-8 space-y-6">
      <Link to="/settings" className="inline-flex items-center gap-1"><ArrowLeft size={18}/> Settings</Link>
      <h1 className="text-2xl font-semibold flex items-center gap-2"><Mail/>Email connections</h1>
      <p>Connect once, then search and read your stored iCloud emails across folders inside NDG.</p>
      <p className="text-sm">This connection works inside NDG. Access from ChatGPT requires a separate authorised connection.</p>
      <Link to="/settings/email-connections?view=chatgpt" className="inline-block underline py-3">ChatGPT email connection</Link>
      <section className="border-y py-5 space-y-4" aria-busy={busy || loading}>
        <h2 className="flex items-center gap-2 font-semibold"><ShieldCheck size={20}/>iCloud Mail</h2>
        {loading ? <p role="status">Checking your saved connection...</p>
        : !statusReady ? <div className="space-y-3">
          <button type="button" onClick={() => setReload((n) => n + 1)} className={button}>Refresh connection</button>
          <Link to="/login" className="block underline">Sign in to NDG</Link>
        </div> : <>
          {status.connected && <><p>Connected as <strong>{status.email}</strong></p>
            <p>Searching and opening emails changes nothing. Sending, replies and read/unread changes use the separate approval controls. Moving and deletion are not enabled.</p></>}
          {(!status.connected || editing) && <form onSubmit={connect} className="space-y-4">
            <label className="block">iCloud email<input required type="email" disabled={busy} value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" className="mt-1 w-full rounded-lg border px-3 py-3 bg-transparent"/></label>
            <label className="block">Apple app-specific password<input required type="password" minLength={8} maxLength={128} disabled={busy} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" className="mt-1 w-full rounded-lg border px-3 py-3 bg-transparent"/></label>
            <p className="text-sm">Use an Apple app-specific password, not your normal Apple password. NDG stores it encrypted so you do not need to enter it for every search.</p>
            <button type="submit" disabled={busy || status.configured === false} className={button}>{busy ? "Connecting..." : "Connect and save iCloud"}</button>
          </form>}
          {status.connected && <>
            <div className="flex flex-wrap gap-3 border-t pt-5">
              <button type="button" disabled={busy} onClick={() => searchDVSA("Part 3")} className={button}>DVSA Part 3</button>
              <button type="button" disabled={busy} onClick={() => searchDVSA("DVSA")} className={button}>All DVSA emails</button>
              <button type="button" disabled={busy} onClick={() => setComposeOpen((value) => !value)} className={button}>{composeOpen ? "Close new email" : "New email"}</button>
            </div>
            {composeOpen && <MailComposer key={status.verified_at} expectedConnection={status.verified_at} />}
            <form onSubmit={search} className="space-y-3">
              <label className="block">Search senders<select disabled={busy} value={senderScope} onChange={(e) => { setSenderScope(e.target.value); setSearchPage(null); setResults([]); setSelected(null); setMessage(""); }} className="mt-1 w-full rounded-lg border px-3 py-3 bg-transparent">
                <option value="dvsa">DVSA only</option><option value="all">All senders</option>
              </select></label>
              <label className="block">Search all email folders<input required maxLength={200} disabled={busy} value={query} onChange={(e) => { setQuery(e.target.value); setSearchPage(null); }} className="mt-1 w-full rounded-lg border px-3 py-3 bg-transparent"/></label>
              <button type="submit" disabled={busy} className={button}>Search emails</button>
              <p className="text-sm">Includes Inbox, Sent, Drafts, Archive, Junk, Deleted and custom folders when present. No date cutoff. Attachment contents are not searched separately. DVSA only filters sender addresses ending in dvsa.gov.uk; it does not verify authenticity. Part 3 also searches Part three and instructional ability. Choose All senders for forwarded messages or other notification addresses.</p>
              <p className="text-sm">Part 2 and Part 3 results include a matching excerpt from the readable subject or message. Large emails that cannot be checked fully are labelled as possible matches.</p>
            </form>
            <div className="divide-y">{results.map((mail) => <button key={mail.id} type="button" disabled={busy} onClick={() => openMessage(mail.id)} className="block w-full text-left py-4 disabled:opacity-50">
              <strong className="block break-words">{mail.subject || "(No subject)"}</strong>
              <span className="block break-words">{mail.from}</span><span className="block text-sm">{mail.date} · {mail.mailbox}</span>
              {mail.relevance?.status === "verified" && <span className="block text-sm mt-2 break-words">Match in {mail.relevance.location}: {mail.relevance.excerpt}</span>}
              {mail.relevance?.status === "unverified" && <span className="block text-sm mt-2 break-words">Possible match — {mail.relevance.reason}</span>}
            </button>)}</div>
            {searchPage && <button type="button" disabled={busy} className={button} onClick={() => runSearch(searchPage.query, searchPage.scope, searchPage.cursor)}>More matching emails</button>}
            {selected && <article className="border-y py-5 space-y-3">
              <h2 className="text-xl font-semibold break-words">{selected.subject || "(No subject)"}</h2>
              <p className="break-words">{selected.from}<br/>{selected.date}</p>
              <pre className="whitespace-pre-wrap break-words font-sans leading-relaxed">{selected.text || "No readable text was found. Open this message in Apple Mail."}</pre>
              {selected.truncated && <p className="font-medium">This message is too large to show fully. Open Apple Mail for the complete message.</p>}
              <MailComposer key={selected.id} sourceId={selected.id} controlsOnly expectedConnection={status.verified_at} />
              <p className="text-sm">Text-only view. Attachments and remote images are not displayed.</p>
            </article>}
            <div className="flex flex-wrap gap-3 border-t pt-5">
              <button type="button" disabled={busy} onClick={() => { setEditing(!editing); setPassword(""); }} className={button}>{editing ? "Cancel update" : "Update connection"}</button>
              <button type="button" disabled={busy} onClick={disconnect} className={button}>Disconnect iCloud</button>
            </div>
          </>}
        </>}
        {message && <p role="status" aria-live="polite">{message}</p>}
      </section>
    </main></div>;
}
